import { CalendarDays, Heart, Users } from "lucide-react";
import type { ReactNode } from "react";
import { AmbientCelebrationBackdrop } from "@/shared/components/ambient-celebration-backdrop";
import { StepProgressBar, type StepProgressItem } from "@/shared/components/step-progress-bar";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";

interface OnboardingRewardProps {
  levelsCompleted: number;
  onDone: () => void;
}

export function OnboardingReward({ levelsCompleted, onDone }: OnboardingRewardProps): ReactNode {
  const progressSteps = buildRewardProgressSteps(levelsCompleted);

  return (
    <div className="relative isolate overflow-hidden p-6">
      <AmbientCelebrationBackdrop />
      <div className="relative flex flex-col gap-6">
        <Card className="mx-auto w-full max-w-md border-transparent bg-pulse-soft text-center shadow-brand">
          <CardHeader className="gap-2">
            <p className="text-sm font-medium text-text-brand">Achievement unlocked</p>
            <CardTitle className="text-2xl text-text-primary">Level 1 complete!</CardTitle>
            <p id="onboarding-reward-description" className="text-sm text-text-secondary">
              Your team is aboard.
            </p>
          </CardHeader>
        </Card>

        <Card className="mx-auto w-full max-w-md gap-3 py-4">
          <CardContent className="flex flex-col gap-3 px-5">
            <StepProgressBar
              ariaLabel="Workspace setup progress"
              steps={progressSteps}
              size="compact"
              shape="rounded"
              surface="subtle"
              nodeContentMode="index"
              showTooltip={false}
            />
            <p className="text-sm text-text-secondary">Next up: Send your first kudos</p>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button
            id="onboarding-reward-done-button"
            type="button"
            variant="pulse"
            size="lg"
            onClick={onDone}
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

function buildRewardProgressSteps(levelsCompleted: number): ReadonlyArray<StepProgressItem> {
  return [
    {
      id: "importTeam",
      label: "Assemble your team",
      detail: "Import teammates to fill your pulse data.",
      state: levelsCompleted >= 1 ? "done" : "current",
      icon: Users,
      stepNumber: 1,
    },
    {
      id: "giveKudos",
      label: "Send your first kudos",
      detail: "Locked until your team is in Pulsewise.",
      state: levelsCompleted >= 2 ? "done" : levelsCompleted >= 1 ? "current" : "upcoming",
      icon: Heart,
      stepNumber: 2,
    },
    {
      id: "dailyCheckIn",
      label: "Launch a daily check-in",
      detail: "Locked until you send your first kudos.",
      state: levelsCompleted >= 3 ? "done" : levelsCompleted >= 2 ? "current" : "upcoming",
      icon: CalendarDays,
      stepNumber: 3,
    },
  ];
}
